import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { catalogsAPI } from '@/api/client'
import { useNotifications } from '@/composables/useNotifications'
import StorageFactory from '@/drivers/storage'
import { t } from '@/i18n'
import { useAuthStore } from '@/store/auth'
import type {
  Catalog,
  CatalogCategory,
  CatalogItems,
  CatalogModifier,
  CatalogModifierGroup,
  CatalogProduct,
  CatalogProductCategory,
  CatalogProductNormalized,
  Catalogs,
  CatalogStockInfo,
  LocationBrandCatalogsBySource,
  PickupType,
  Source,
  SourceCatalog
} from '@/types'

const LOW_STOCK_THRESHOLD_DEFAULT = 5

export const useCatalogStore = defineStore(
  'catalog',
  () => {
    const catalogs = ref<Record<string, Catalog>>({})
    const categories = ref<Record<string, CatalogCategory>>({})
    const defaultPosCatalog = ref<string | null>(null)
    const products = ref<Record<string, CatalogProduct>>({})
    const modifierGroups = ref<Record<string, Record<string, CatalogModifierGroup>>>({})
    const modifiers = ref<Record<string, Record<string, CatalogModifier>>>({})
    const productCategories = ref<Record<string, CatalogProductCategory>>({})
    const locationBrandCatalogsBySource = ref<LocationBrandCatalogsBySource>({})
    const stockByOrganizationProductId = ref<Record<string, CatalogStockInfo>>({})

    const { notifyInfo } = useNotifications()

    const productsByCatalog = computed(() => (catalogId: string | null) => {
      const catalogProducts = catalogId
        ? catalogs.value[catalogId].categories.flatMap(c => categories.value[c].products)
        : Object.keys(products.value)

      const uniqueProductIds = Array.from(new Set(catalogProducts))

      return uniqueProductIds.map(id => products.value[id])
    })

    const getCatalogIdByTabVirtualBrandId = computed(
      () => (tabVirtualBrandId: string, pickupType?: PickupType, source?: Source) => {
        if (!tabVirtualBrandId) return defaultPosCatalog.value

        let typeOfCatalog: keyof SourceCatalog | null = null
        if (pickupType === 'delivery' || pickupType === 'ownDelivery')
          typeOfCatalog = 'deliveryCatalogId'
        else if (pickupType === 'takeAway') typeOfCatalog = 'takeawayCatalogId'

        let catalogId: string | null = null
        if (typeOfCatalog) {
          const locationVirtualBrandAssignments =
            locationBrandCatalogsBySource.value[tabVirtualBrandId]
          source = source!.toLowerCase() as Source

          catalogId =
            locationVirtualBrandAssignments[source]?.[typeOfCatalog] ??
            locationVirtualBrandAssignments['default']?.[typeOfCatalog]
        }

        return catalogId || defaultPosCatalog.value
      }
    )

    const allCourses = computed(() => {
      return (Object.values(catalogs.value)[0]?.courses ?? []).map(course => ({
        label: course,
        value: course
      }))
    })

    async function refreshCatalogs(inputCatalogs?: Catalogs) {
      const auth = useAuthStore()
      if (!auth.isAuthenticated) return
      if (!inputCatalogs?.catalogs) {
        inputCatalogs = await catalogsAPI.getCatalogs().catch(() => undefined)
      }
      if (!inputCatalogs) {
        inputCatalogs = await StorageFactory.getStorage()
          .getItem<Catalogs>('catalogs')
          .then(c => c ?? undefined)
      }
      if (!inputCatalogs) return

      StorageFactory.getStorage().setItem('catalogs', inputCatalogs)

      catalogs.value = inputCatalogs.catalogs.reduce(
        (acc, catalog) => {
          acc[catalog.catalog.id] = catalog.catalog
          return acc
        },
        {} as Record<string, Catalog>
      )

      stockByOrganizationProductId.value = inputCatalogs.locationStock
      const productsTmp: { [key: string]: CatalogProduct } = {}
      const categoriesTmp: { [key: string]: CatalogCategory } = {}
      const productCategoriesTmp: { [key: string]: CatalogProductCategory } = {}
      const modifierGroupsTmp: {
        [key: string]: { [key: string]: CatalogModifierGroup }
      } = {}
      const modifiersTmp: {
        [key: string]: { [key: string]: CatalogModifier }
      } = {}
      for (const catalog of inputCatalogs.catalogs) {
        const catalogId = catalog.catalog.id
        Object.assign(modifierGroupsTmp, { [catalogId]: {} })
        Object.assign(modifiersTmp, { [catalogId]: {} })
        Object.assign(productsTmp, catalog.products)
        Object.assign(categoriesTmp, catalog.categories)
        Object.assign(productCategoriesTmp, catalog.productCategories)
        Object.assign(modifierGroupsTmp[catalogId], catalog.modifierGroups)
        Object.assign(modifiersTmp[catalogId], catalog.modifiers)
      }

      modifiers.value = modifiersTmp
      modifierGroups.value = modifierGroupsTmp
      productCategories.value = productCategoriesTmp
      products.value = productsTmp
      categories.value = categoriesTmp

      defaultPosCatalog.value = inputCatalogs.defaultPosCatalog
      locationBrandCatalogsBySource.value = inputCatalogs.locationBrandCatalogsBySource

      Object.entries(stockByOrganizationProductId.value).forEach(([orgId, stockInfo]) => {
        setProductStock(orgId, stockInfo)
      })
    }

    async function refreshCatalog(
      catalog: CatalogItems,
      locationStock: Record<string, CatalogStockInfo>
    ) {
      const auth = useAuthStore()
      if (!auth.isAuthenticated) return
      if (!catalog) return

      const catalogId = catalog.catalog.id

      catalogs.value[catalogId] = { ...catalog.catalog }

      products.value = { ...products.value, ...(catalog.products || {}) }
      stockByOrganizationProductId.value = locationStock
      Object.entries(stockByOrganizationProductId.value).forEach(([orgId, stockInfo]) => {
        setProductStock(orgId, stockInfo)
      })
      categories.value = { ...categories.value, ...(catalog.categories || {}) }
      productCategories.value = {
        ...productCategories.value,
        ...(catalog.productCategories || {})
      }
      modifierGroups.value = {
        ...modifierGroups.value,
        [catalogId]: { ...(catalog.modifierGroups || {}) }
      }
      modifiers.value = {
        ...modifiers.value,
        [catalogId]: { ...(catalog.modifiers || {}) }
      }
    }

    function getProductById(id: string, productCatalogId?: string) {
      const catalogProduct = { ...products.value[id] }
      const catalogId = productCatalogId ?? catalogProduct?.catalogId
      const product: CatalogProductNormalized = {
        ...catalogProduct,
        categories: catalogProduct.categories ? [] : undefined,
        modifierGroups: catalogProduct.modifierGroups ? [] : undefined
      }
      if (!catalogId) return product
      if (catalogProduct.modifierGroups) {
        product.modifierGroups = catalogProduct.modifierGroups.map((modifierGroupId, position) => {
          const catalogModifierGroup = modifierGroups.value[catalogId][modifierGroupId]
          const modifierGroup = {
            ...catalogModifierGroup,
            position,
            modifiers: [] as CatalogModifier[]
          }
          if (catalogModifierGroup) {
            modifierGroup.modifiers = catalogModifierGroup.modifiers.map(modifierId => {
              return { ...modifiers.value[catalogId][modifierId] }
            })
          }
          return modifierGroup
        })
      }
      if (catalogProduct.categories) {
        product.categories = catalogProduct.categories.map(categoryId => {
          const catalogCategory = productCategories.value[categoryId]
          const category = {
            ...catalogCategory,
            products: [] as CatalogProductNormalized[]
          }
          if (catalogCategory) {
            category.products = catalogCategory.products.map(productId => {
              return getProductById(productId, catalogId)
            })
          }
          return category
        })
      }
      return product
    }

    function getProductsByOrganizationProductId(organizationProductId: string) {
      const catalogProducts = Object.values(products.value).filter(
        product => product.organizationProductId === organizationProductId
      )
      return catalogProducts
    }

    function updateProductStock(productId: string, stockDiff: number) {
      const product = products.value[productId]
      const orgId = product?.organizationProductId
      if (!orgId || !stockByOrganizationProductId.value[orgId]) return

      const currentStockInfo = stockByOrganizationProductId.value[orgId]
      const newStock = Math.max(0, currentStockInfo.stock + stockDiff)

      setProductStock(orgId, { ...currentStockInfo, stock: newStock })
    }

    function setProductStock(organizationProductId: string, stockInfo: CatalogStockInfo) {
      if (!organizationProductId) return

      const oldStockInfo = stockByOrganizationProductId.value[organizationProductId]

      const lowStockThreshold = oldStockInfo?.lowStockThreshold ?? LOW_STOCK_THRESHOLD_DEFAULT
      const wasLowStockBefore = oldStockInfo
        ? oldStockInfo.stock > 0 && oldStockInfo.stock < lowStockThreshold
        : false

      stockByOrganizationProductId.value[organizationProductId] = stockInfo

      const affectedProducts = getProductsByOrganizationProductId(organizationProductId)
      if (!affectedProducts.length) return

      const newLowStockThreshold = stockInfo.lowStockThreshold ?? LOW_STOCK_THRESHOLD_DEFAULT
      const hasStockNow = stockInfo.stock > 0
      const isLowStockNow = hasStockNow && stockInfo.stock < newLowStockThreshold

      if (isLowStockNow && !wasLowStockBefore) {
        notifyInfo({
          icon: 'alert-circle',
          title: t('menu-availability.stock.low-stock'),
          description: t('menu-availability.stock.product', { name: affectedProducts[0]?.name })
        })
      }

      for (const product of affectedProducts) {
        if (products.value[product.id]) {
          products.value[product.id].hasStock = hasStockNow
          products.value[product.id].hasLowStock = isLowStockNow
        }
      }
    }

    return {
      catalogs,
      categories,
      defaultPosCatalog,
      products,
      modifierGroups,
      modifiers,
      productCategories,
      locationBrandCatalogsBySource,
      productsByCatalog,
      getCatalogIdByTabVirtualBrandId,
      allCourses,
      stockByOrganizationProductId,
      refreshCatalogs,
      refreshCatalog,
      getProductById,
      updateProductStock,
      setProductStock
    }
  },
  {
    persist: false
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCatalogStore, import.meta.hot))
}
